import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('popstate', this.#handlePopState);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('popstate', this.#handlePopState);
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  /**
   * A bfcache restore (e.g. backing out of checkout) brings the page back with
   * the dialog open and the body still position-locked from `showDialog`.
   * Reset instantly — no closing animation — so the restored page is usable.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;
    const { dialog } = this.refs;
    if (!dialog.open) {
      // iOS Safari can restore the snapshot with the dialog already dropped
      // from the top layer but the body still position-locked from showDialog.
      // Nothing is open, so nothing should be locked.
      if (document.body.style.position === 'fixed') this.#unlockBody();
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.remove('dialog-closing');
    dialog.close();
    this.#unlockBody();

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * A dialog that owns a history entry (attribute `history-entry`) is closed by
   * the browser's back gesture instead of the gesture leaving the page with the
   * body still locked. Any popstate that lands on a state that is not ours
   * while we are open means "close".
   * @param {PopStateEvent} event
   */
  #handlePopState = (event) => {
    if (!this.refs.dialog.open) return;
    if (this.ownsHistoryEntry && event.state?.dialogId !== this.id) {
      this.#close();
    }
  };

  get ownsHistoryEntry() {
    return this.hasAttribute('history-entry') && !!this.id;
  }

  #unlockBody() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    if (this.ownsHistoryEntry && history.state?.dialogId !== this.id) {
      try {
        history.pushState({ ...(history.state || {}), dialogId: this.id }, '', location.href);
      } catch {
        /* ignore: the dialog still opens, it just will not own a history entry */
      }
    }

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      // The page went to the back/forward cache between the call and this
      // frame (a back gesture mid-open): locking now would freeze the page
      // when it is restored with the dialog gone.
      if (document.visibilityState === 'hidden') return;

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // Our own history entry is on top: go back, and let popstate do the close
    // so the history stack and the dialog never disagree.
    if (this.ownsHistoryEntry && history.state?.dialogId === this.id) {
      history.back();
      return;
    }

    await this.#close();
  };

  #close = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    this.#unlockBody();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement && event.target.hasAttribute('scroll-lock')) {
      // Derive the lock from current DOM state instead of trusting this
      // event's open/closed direction — toggle events are queued async, so
      // rapid open/close sequences can deliver them out of order and leave
      // the page locked with nothing open (or vice versa).
      if (document.querySelector('details[scroll-lock][open]')) {
        document.documentElement.setAttribute('scroll-lock', '');
      } else {
        document.documentElement.removeAttribute('scroll-lock');
      }
    }
  },
  { capture: true }
);

// Safety net: never let the page stay scroll-locked without a visible reason.
// On bfcache restores (e.g. backing out of checkout) browsers may drop a
// dialog's top-layer state while the snapshot keeps the body's scroll-lock
// styles and any open scroll-lock drawers. And should any code path ever leak
// the scroll-lock attribute with nothing actually open, reconcile it away on
// every page show rather than leaving the page frozen.
/**
 * Drop a body lock nothing justifies. Runs on every page show, every history
 * move and every return to the foreground: a modal dialog that the browser
 * dropped (back/forward cache, iOS back gesture) must never leave the page
 * frozen until a reload.
 */
/**
 * Is any dialog actually showing as a modal? A restored snapshot can keep the
 * `open` attribute on a dialog the browser already dropped from the top layer;
 * that dialog blocks nothing, so it must not justify a lock either.
 */
function hasModalDialog() {
  try {
    return !!document.querySelector('dialog[open]:modal');
  } catch {
    return !!document.querySelector('dialog[open]');
  }
}

function reconcileBodyLock() {
  if (document.body.style.position === 'fixed' && !hasModalDialog()) {
    const lockedScrollY = -parseFloat(document.body.style.top) || 0;
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
  }
  if (
    document.documentElement.hasAttribute('scroll-lock') &&
    !document.querySelector('details[scroll-lock][open]') &&
    !hasModalDialog()
  ) {
    document.documentElement.removeAttribute('scroll-lock');
  }
}
window.addEventListener('popstate', () => setTimeout(reconcileBodyLock, 0));
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') reconcileBodyLock();
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    // Restored snapshots come back with drawers/dialogs mid-state — close them.
    document.querySelectorAll('details[scroll-lock][open]').forEach((details) => {
      details.removeAttribute('open');
    });
    document.documentElement.removeAttribute('scroll-lock');
  }
  reconcileBodyLock();
});
